import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Warning } from "@phosphor-icons/react";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
}

/**
 * While a calendar-scheduled MCQ is in its mandatory window and not yet attempted,
 * the student may only open that test's URL — not the rest of the panel.
 */
export default function StudentMandatoryExamGate({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tests, setTests] = useState([]);

  const refresh = useCallback(() => {
    api.get("/tests/available").then((r) => setTests(r.data)).catch(() => setTests([]));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, location.pathname]);

  useEffect(() => {
    const t = setInterval(refresh, 45000);
    return () => clearInterval(t);
  }, [refresh]);

  const mandatory = tests.filter((t) => !t.attempted && t.scheduled_at);
  const mandatoryIds = new Set(mandatory.map((t) => t.id));

  const m = location.pathname.match(/^\/student\/tests\/([^/]+)$/);
  const onAllowedTest = m && mandatoryIds.has(m[1]);

  if (mandatory.length === 0 || onAllowedTest) {
    return children;
  }

  return (
    <div className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-6 overflow-y-auto bg-bone/95 px-6 py-10 backdrop-blur-sm">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="mandatory-exam-title"
        aria-describedby="mandatory-exam-desc"
        className="max-w-lg w-full shrink-0 border border-terracotta/50 bg-ivory shadow-xl p-8 rounded-sm my-auto"
        onKeyDown={(e) => {
          if (e.key === "Escape") e.preventDefault();
        }}
      >
        <div className="flex gap-3 mb-4">
          <Warning className="shrink-0 text-terracotta" size={28} weight="fill" aria-hidden />
          <div>
            <h1 id="mandatory-exam-title" className="font-serif text-2xl text-forest leading-tight">
              Scheduled test — action required
            </h1>
            <p id="mandatory-exam-desc" className="text-sm text-mossy mt-2 leading-relaxed">
              Your teacher scheduled a test that you must take during the allowed time. Other pages are locked until you
              open the test below or the window ends. You cannot dismiss this screen.
            </p>
          </div>
        </div>
        <ul className="space-y-3 mb-6">
          {mandatory.map((t) => (
            <li key={t.id} className="border border-stone p-4 bg-sand/40">
              <div className="font-medium text-forest">{t.title}</div>
              <div className="text-xs font-mono text-mossy mt-1">
                Opens {formatWhen(t.scheduled_at)}
                {t.scheduled_until && (
                  <>
                    {" "}
                    · Complete by {formatWhen(t.scheduled_until)}
                  </>
                )}
              </div>
              <button
                type="button"
                className="btn-primary mt-3 w-full sm:w-auto text-sm px-5 py-2"
                onClick={() => navigate(`/student/tests/${t.id}`)}
              >
                Attempt now
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs text-mossy border-t border-stone pt-4">
          If the deadline passes without an attempt, this test will no longer be available from Take tests.
        </p>
        <button type="button" onClick={() => logout()} className="mt-4 text-xs text-mossy hover:text-terracotta underline">
          Sign out
        </button>
      </div>
    </div>
  );
}
